/**
 * Utility for Date Time Formatting
 */

import { format, parse } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

export type DateTimeType = 'date' | 'datetime' | 'time';

export const defaultDateFormat = 'dd/MM/yyyy';
export const defaultDateTimeFormat = 'dd/MM/yyyy HH:mm';
export const defaultTimeFormat = 'HH:mm';
export const transactionDateStringFormat = 'dd/MM/yyyy HH:mm:ss';
export const defaultTimeZone = 'Europe/London';

type DateTimeFormatMap = {
  [key in DateTimeType]: string;
};

export const dateTimeFormatMap: DateTimeFormatMap = {
  date: defaultDateFormat,
  datetime: defaultDateTimeFormat,
  time: defaultTimeFormat,
};

const formatUnixTimestampToGMTBST = (unixTimestamp: number, formatString: string) => {
  if (!unixTimestamp) return '';

  return formatInTimeZone(unixTimestamp * 1000, defaultTimeZone, formatString);
};

const formatDateToGMTBST = (inputDate: Date, formatString: string) => {
  if (!inputDate) return '';

  return formatInTimeZone(inputDate, defaultTimeZone, formatString);
};

const formatDateStringToGMTBST = (inputDateString: string, formatString: string) => {
  const inputDate = new Date(inputDateString);

  if (!inputDateString) return '';

  return formatInTimeZone(inputDate, defaultTimeZone, formatString);
};

const formatTransactionDateStringToGMTBST = (inputDateString: string, formatString: string) => {
  const inputDate = parse(inputDateString, transactionDateStringFormat, new Date());

  if (!inputDateString) return '';

  return formatInTimeZone(inputDate, defaultTimeZone, formatString);
};

export const getUnixTimeStamp = (date: Date): number => Math.floor(+date / 1000);
export const getCurrentUnixTimestamp = () => Math.floor(+new Date() / 1000);
export const convertUnixTimeStampToDate = (timestamp: number): Date => new Date(timestamp * 1000);

/**
 * Constructs a Date from an arbitrary dateString
 * @param dateString e.g. "23/01/2024 15:30"
 * @param dateStringFormat e.g. "dd/MM/yyyy HH:mm" (though in this example prefer using defaultDateTimeFormat from dateTimeFormatter.ts)
 * @returns A Date object set to the time specified in the dateString
 */
export const dateStringToDate = (dateString: string, dateStringFormat: string) =>
  parse(dateString, dateStringFormat, new Date());

export const timestampToTransactionDateString = (timestamp: number) =>
  formatUnixTimestampToGMTBST(timestamp, transactionDateStringFormat);

export const transactionDateStringToUnixTimestamp = (transactionDateString: string) => {
  return Math.floor(+parse(transactionDateString, transactionDateStringFormat, new Date()) / 1000);
};

// -------------------------------------------------------------------

// USE THESE FUNCTIONS BELOW TO FORMAT YOUR DATES
/**
 * Formats a Unix timestamp into a GMT/BST formatted string using the specified format.
 *
 * @param {number} timestamp - The Unix timestamp to format.
 * @param {DateTimeType} format - The format to use for the formatted string.
 * @return {string} The formatted GMT/BST string.
 */
export const formatTimestamp = (timestamp: number, format: DateTimeType): string =>
  formatUnixTimestampToGMTBST(timestamp, dateTimeFormatMap[format]);

/**
 * Formats a given date into a GMT/BST formatted string using the specified format.
 *
 * @param {Date} date - The date to be formatted.
 * @param {DateTimeType} format - The format to use for the formatted string.
 * @return {string} The formatted GMT/BST string.
 */
export const formatDate = (date: Date, format: DateTimeType): string =>
  formatDateToGMTBST(date, dateTimeFormatMap[format]);

/**
 * Formats a given date string into a GMT/BST formatted string using the specified format.
 *
 * @param {string} dateString - The date string to be formatted.
 * @param {DateTimeType} format - The format to use for the formatted string.
 * @return {string} The formatted GMT/BST string.
 */
export const formatDateString = (dateString: string, format: DateTimeType): string =>
  formatDateStringToGMTBST(dateString, dateTimeFormatMap[format]);

/**
 * Formats a transaction date string to the specified format.
 *
 * @param {string} transactionDateString - The transaction date string to format.
 * @param {DateTimeType} format - The format to which the date string should be converted.
 * @return {string} The formatted transaction date string.
 */
export const formatTransactionDateString = (
  transactionDateString: string,
  formatType: DateTimeType,
  applyTimeZone = true,
): string => {
  const inputDate = parse(transactionDateString, transactionDateStringFormat, new Date());

  if (transactionDateString && !applyTimeZone) {
    return format(inputDate, dateTimeFormatMap[formatType]);
  }

  return formatTransactionDateStringToGMTBST(transactionDateString, dateTimeFormatMap[formatType]);
};

/**
 * Formats a given Unix timestamp into a GMT/BST formatted string using the 'datetime' format.
 *
 * @param {number | undefined} value - The Unix timestamp to format.
 * @return {string} The formatted GMT/BST string, or an empty string if the value is undefined.
 */
export const formatDateAndTime = (value: number | undefined) => {
  if (value) return formatTimestamp(value / 1000, 'datetime');
  return '';
};
